import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { collection, query, where, getDocs, orderBy, doc, updateDoc, deleteDoc, getDoc, onSnapshot, addDoc } from 'firebase/firestore';
import { isMobileDevice, getMobileRestrictionMessage } from '../utils/mobileRestrictions';
import MobileRestriction from '../components/MobileRestriction';
import { auth, db } from '../utils/firebase';
// Removed entitlements import - not needed for simplified MVP
import { startGameLaunchCallable } from '../utils/gamelaunch';
import HostLayout from '../layouts/HostLayout';
import useLaunchCountdown from '../hooks/useLaunchCountdown';
import { getLaunchStatus } from '../api/sessionClient';
import { startLaunchSession, getLaunchStatus as getUnifiedLaunchStatus, createOverageCheckout } from '../services/launchService';
import { createCreatedGameLaunchCheckout } from '../utils/stripeClient';

// Refined Launch Button Component
const LaunchButton = ({ purchaseId, gameId, onNavigateLaunch, onOpenRepurchase, navigate, isCreatedGame = false }) => {
  const { session, remaining, isActive, isExpired } = useLaunchCountdown(gameId);
  const [launchStatus, setLaunchStatus] = useState(null);
  const [showMobileRestriction, setShowMobileRestriction] = useState(false);
  const { user, ready, claims } = useAuth();

  // Check launch status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const current = await getUnifiedLaunchStatus(gameId);
        setLaunchStatus(current.status);
        
        // Clear localStorage cache if session is completed
        if (current.status === 'completed') {
          localStorage.removeItem(`launchSession_${gameId}`);
        }
      } catch (error) {
        console.error('Error checking launch status:', error);
      }
    };
    checkStatus();
  }, [gameId]);

  const handleClick = async () => {
    // Check if user is on mobile device
    if (isMobileDevice()) {
      setShowMobileRestriction(true);
      return;
    }
    
    try {
      // Always check server for current status using unified service
      const current = await getUnifiedLaunchStatus(gameId);
      setLaunchStatus(current.status);

      if (current.status === "none") {
        // For created games, always require $15 payment
        if (isCreatedGame) {
          const ok = window.confirm(
            "Launch this game for $15? This includes a 12-hour launch window."
          );
          if (!ok) return;
          
          // Redirect to $15 checkout for created games
          const checkoutUrl = await createCreatedGameLaunchCheckout(gameId);
          window.location.href = checkoutUrl;
          return;
        }

        // For marketplace games, check if they have launch rights
        if (current.entitlement?.requiresOverage) {
          const ok = window.confirm(
            "You have no launches remaining for this marketplace game. Buy again to launch?"
          );
          if (!ok) return;
          
          // Redirect to marketplace repurchase
          onOpenRepurchase?.(gameId);
          return;
        }

        const ok = window.confirm(
          "Start 12-hour launch window? This will use one of your available launches and cannot be paused. You have 12 hours to utilize the launch or until you submit final scores. Continue?"
        );
        if (!ok) return;
        
        // Use the unified launch service for all games
        const newSession = await startLaunchSession(gameId);
        
        // Cache session data locally for countdown with isNewSession flag
        const sessionData = {
          sessionId: newSession.sessionId,
          startedAt: newSession.startedAt,
          expiresAt: newSession.expiresAt,
          status: 'active',
          isNewSession: true
        };
        localStorage.setItem(`launchSession_${gameId}`, JSON.stringify(sessionData));
        
        // Navigate with session data to show warning modal
        navigate(`/launch-game/${gameId}`, { state: { session: sessionData } });
        return;
      }

      if (current.status === "active") {
        const ok = window.confirm(`Window active. Time remaining: ${remaining || 'checking…'}. Continue?`);
        if (!ok) return;
        
        // Update local cache with server data
        if (current.session) {
          const sessionData = {
            ...current.session,
            isNewSession: false
          };
          localStorage.setItem(`launchSession_${gameId}`, JSON.stringify(sessionData));
          
          // Navigate with session data
          navigate(`/launch-game/${gameId}`, { state: { session: sessionData } });
        } else {
          onNavigateLaunch?.(gameId, current.session?.sessionId);
        }
        return;
      }

      if (current.status === "completed") {
        // Game has been completed - allow relaunch
        const ok = window.confirm(
          "This game has been completed. Relaunching will start a new 12-hour session and use one of your available launches. Continue?"
        );
        if (!ok) return;
        
        // Use the smart relaunch-aware function for all games
        const res = await startGameLaunchCallable({ gameId, dryRun: false });
        console.log('[LAUNCH] callable response:', res);
        const { sessionId } = res || {};
        
        // No entitlements to update in simplified MVP
        
        // Navigate with sessionId and bypass resume guard
        if (res?.ok && sessionId) {
          navigate(`/launch-game/${gameId}?session=${encodeURIComponent(sessionId)}&force=1&skipResume=1`);
        } else {
          // show human message based on response
          alert('Failed to start game session');
        }
        return;
      }

      if (current.status === "expired") {
        // Clear expired session from cache
        localStorage.removeItem(`launchSession_${gameId}`);
        onOpenRepurchase?.(purchaseId, gameId);
        return;
      }

    } catch (error) {
      console.error('Error in launch button:', error);
      
      // Handle specific error codes from the smart relaunch function
      if (error.message.includes('409 already-active')) {
        const sessionId = error.message.match(/sessionId: (\w+)/)?.[1];
        const choice = window.confirm(
          `An active session already exists for this game. Would you like to:\n\n` +
          `• Click OK to resume the existing session\n` +
          `• Click Cancel to start a new session (this will terminate the current one)`
        );
        
        if (choice) {
          // Resume existing session
          const sessionData = {
            sessionId: sessionId,
            status: 'active',
            isNewSession: false
          };
          localStorage.setItem(`launchSession_${gameId}`, JSON.stringify(sessionData));
          navigate(`/launch-game/${gameId}`, { state: { session: sessionData } });
        } else {
          // Start new session with forceNew
          try {
            const res = await startGameLaunchCallable({ gameId, dryRun: false });
            console.log('[LAUNCH] forceNew callable response:', res);
            const { sessionId } = res || {};
            
            // No entitlements to update in simplified MVP
            
            // Navigate with sessionId and bypass resume guard
            if (res?.ok && sessionId) {
              navigate(`/launch-game/${gameId}?session=${encodeURIComponent(sessionId)}&force=1&skipResume=1`);
            } else {
              window.alert('Unable to Start New Session\n\nWe couldn\'t start a new session for this game. Please try resuming the existing session or contact support if this continues.');
            }
          } catch (forceError) {
            const errorMsg = forceError.code === 'unavailable' || forceError.message?.includes('network')
              ? 'Unable to connect to the server. Please check your internet connection and try again.'
              : forceError.message || 'We couldn\'t start a new session. Please try again.';
            window.alert(`Unable to Start New Session\n\n${errorMsg}`);
          }
        }
        return;
      }
      
      if (error.message.includes('403 no-launches')) {
        window.alert('No Launches Remaining\n\nYou\'ve used all your launches for this game. Click "Repurchase" to buy another launch and continue playing.');
        onOpenRepurchase?.(purchaseId, gameId);
        return;
      }
      
      if (error.message.includes('403 not-entitled')) {
        window.alert('Game Access Required\n\nYou don\'t have access to launch this game. Please make sure you\'ve purchased it from the marketplace.');
        return;
      }
      
      // Generic error fallback with user-friendly message
      let errorMessage = 'We couldn\'t launch your game right now.';
      if (error.code === 'unavailable' || error.message?.includes('network') || error.message?.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to our server. Please check your internet connection and try again.';
      } else if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        errorMessage = 'You don\'t have permission to launch this game. Please make sure you\'re logged in and try again.';
      } else if (error.message?.includes('CORS')) {
        errorMessage = 'There was a connection issue. Please try again in a moment.';
      } else {
        errorMessage = error.message || 'Something went wrong. Please try again, or contact support if this continues.';
      }
      
      window.alert(`Unable to Launch Game\n\n${errorMessage}`);
    }
  };

  const handleRepurchase = async () => {
    try {
      // For purchased games, we need to create a new checkout session
      if (!isCreatedGame) {
        // This is a marketplace game - create new checkout
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const createMarketplaceGameCheckout = httpsCallable(functions, 'createMarketplaceGameCheckout');
        
        const result = await createMarketplaceGameCheckout({ gameId });
        
        if (result.data?.url) {
          window.location.href = result.data.url;
        } else {
          throw new Error('Failed to create repurchase checkout');
        }
      } else {
        // This is a created game - create launch checkout
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const createLaunchRightCheckout = httpsCallable(functions, 'createLaunchRightCheckout');
        
        const result = await createLaunchRightCheckout({ gameId });
        
        if (result.data?.url) {
          window.location.href = result.data.url;
        } else {
          throw new Error('Failed to create repurchase checkout');
        }
      }
    } catch (error) {
      console.error('Error creating repurchase checkout:', error);
      
      // Handle specific error cases with user-friendly messages
      let errorMessage = 'We couldn\'t process your repurchase right now.';
      
      if (error.code === 'unavailable' || error.message?.includes('network') || error.message?.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to our payment server. Please check your internet connection and try again.';
      } else if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        errorMessage = 'You don\'t have permission to repurchase this game. Please make sure you\'re logged in and try again.';
      } else if (error.message?.includes('CORS')) {
        errorMessage = 'There was a connection issue. Please try again in a moment.';
      } else {
        errorMessage = 'Something went wrong while processing your repurchase. Please try again, or contact support if the problem continues.';
      }
      
      window.alert(`Repurchase Failed\n\n${errorMessage}`);
    }
  };

  const getButtonProps = () => {
    if (launchStatus === 'completed') {
      return {
        label: 'Repurchase',
        className: 'px-3 py-1 rounded text-sm transition-colors bg-blue-600 text-white hover:bg-blue-700',
        disabled: false,
        onClick: handleRepurchase
      };
    }
    
    if (isActive) {
      return {
        label: `Continue (${remaining || "…"})`,
        className: 'px-3 py-1 rounded text-sm transition-colors bg-green-600 text-white hover:bg-green-700',
        disabled: false,
        onClick: handleClick
      };
    }
    
    if (isExpired) {
      return {
        label: 'Buy Again',
        className: 'px-3 py-1 rounded text-sm transition-colors bg-orange-600 text-white hover:bg-orange-700',
        disabled: false,
        onClick: handleClick
      };
    }
    
    // Check if overage is required (will be checked in handleClick)
    return {
      label: 'Launch',
      className: 'px-3 py-1 rounded text-sm transition-colors bg-green-600 text-white hover:bg-green-700',
      disabled: false,
      onClick: handleClick
    };
  };

  const buttonProps = getButtonProps();
  const disabled = !user || !ready || !gameId || buttonProps.disabled;

  return (
    <>
      <div className="flex items-center gap-2">
        {isActive && launchStatus !== 'completed' && (
          <span className="text-xs rounded px-2 py-1 bg-yellow-100 text-yellow-800">⏳ {remaining || "—"}</span>
        )}
        {launchStatus === 'completed' && (
          <span className="text-xs rounded px-2 py-1 bg-green-100 text-green-800">✅ Completed</span>
        )}
        <button 
          className={buttonProps.className}
          onClick={buttonProps.onClick}
          disabled={disabled}
        >
          {buttonProps.label}
        </button>
      </div>
      
      {showMobileRestriction && (
        <MobileRestriction 
          feature="game_launch"
          onClose={() => setShowMobileRestriction(false)}
        />
      )}
    </>
  );
};

// GameCard component for displaying individual games
const GameCard = ({ game, type, onPreview, onEdit, onSubmitToMarketplace, onDelete, onLaunch, launchWindow, navigate, user, claims }) => {
  const getStatusBadge = (game) => {
    if (type === 'purchased') {
      return <span className="bg-green-600 text-white px-2 py-1 rounded text-xs">Purchased</span>;
    }
    
    const status = game.status || game.approvalStatus;
    switch (status) {
      case 'pending_approval':
      case 'pending':
        return <span className="bg-yellow-600 text-white px-2 py-1 rounded text-xs">Pending</span>;
      case 'approved':
        return <span className="bg-green-600 text-white px-2 py-1 rounded text-xs">Approved</span>;
      case 'rejected':
        return <span className="bg-red-600 text-white px-2 py-1 rounded text-xs">Rejected</span>;
      default:
        return <span className="bg-gray-600 text-white px-2 py-1 rounded text-xs">Draft</span>;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400';
      case 'hard': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  // Extract enriched fields for purchased games
  const title = type === 'purchased' ? (game.title || game.displayTitle || "Unknown game") : game.title;
  const img = type === 'purchased' ? (game.thumbnailUrl || game.previewImage || null) : null;
  
  // Determine launch status for purchased games using new system
  let canLaunch = false;
  let launchButtonText = '';
  let launchStatus = 'not_started';
  
  if (type === 'purchased' && launchWindow) {
    launchStatus = launchWindow.status;
    
    console.log('🎮 GameCard debug for game:', game.gameId);
    console.log('🎮 Launch window data:', launchWindow);
    
    switch (launchWindow.status) {
      case 'not_started':
        canLaunch = true;
        launchButtonText = 'Launch';
        break;
      case 'active':
        canLaunch = true;
        const timeRemaining = { displayText: "00:00:00" }; // launchSessionManager.formatTimeRemaining(launchWindow.expiresAt);
        launchButtonText = `Continue (${timeRemaining.displayText})`;
        break;
      case 'completed':
        canLaunch = false;
        launchButtonText = 'Completed';
        break;
      case 'expired':
        canLaunch = false;
        launchButtonText = 'Buy Again';
        break;
      default:
        canLaunch = false;
        launchButtonText = 'Launch';
    }
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* Game Image or Placeholder */}
      <div className="h-48 bg-gray-700 flex items-center justify-center overflow-hidden">
        {img ? (
          <img 
            src={img} 
            alt={title} 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`text-gray-400 text-4xl ${img ? 'hidden' : 'flex'} items-center justify-center`}>
          🎮
        </div>
      </div>

      {/* Game Info */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-semibold text-white truncate">
            {title}
          </h3>
          {getStatusBadge(game)}
        </div>

        {(game.description || game.gameDescription) && (
          <p className="text-gray-400 text-sm mb-4 line-clamp-2">
            {type === 'purchased' ? (game.description || game.gameDescription) : game.description}
          </p>
        )}

        <div className="flex items-center justify-between mb-4">
          <span className={`text-sm font-medium ${getDifficultyColor(game.difficulty)}`}>
            {type === 'purchased' ? (
              launchWindow?.status === 'active' ? 
                `Active session — 00:00:00 left` : // launchSessionManager.formatTimeRemaining(launchWindow.expiresAt).displayText} left` :
                launchWindow?.status === 'expired' ? 'Expired' :
                'Ready to launch'
            ) : (
              game.difficulty?.charAt(0).toUpperCase() + game.difficulty?.slice(1) || 'Medium'
            )}
          </span>
          <span className="text-gray-400 text-sm">
            {type === 'purchased' ? 'Purchased' : `${game.categories?.length || 0} categories`}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onPreview(type === 'purchased' ? game.gameId : game.id)}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
          >
            Preview
          </button>
          
          {type === 'purchased' && (
            <LaunchButton
              purchaseId={game.id}
              gameId={game.id} // Use document ID as gameId
              navigate={navigate}
              isCreatedGame={false} // This is a marketplace game
              onNavigateLaunch={(gameId, sessionToken) => {
                navigate(`/launch-game/${gameId}`, { state: { sessionToken }});
              }}
              onOpenRepurchase={(purchaseId, gameId) => {
                // LaunchButton will handle repurchase internally via handleRepurchase
                console.log('Repurchase triggered for:', purchaseId, gameId);
              }}
            />
          )}
          
          {type !== 'purchased' && !(type === 'pending' && game.visibility === 'marketplace' && !claims?.admin) && (
            <button
              onClick={() => onEdit(type === 'purchased' ? game.id : game.id)}
              className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-500 transition-colors"
            >
              Edit
            </button>
          )}

          {type === 'created' && (
            <>
              <LaunchButton
                purchaseId={game.id}
                gameId={game.id}
                navigate={navigate}
                isCreatedGame={true}
                onNavigateLaunch={(gameId, sessionToken) => {
                  navigate(`/launch-game/${gameId}`, { state: { sessionToken }});
                }}
                onOpenRepurchase={(purchaseId, gameId) => {
                  // Not applicable for created games
                  console.log('Repurchase not applicable for created games');
                }}
              />
              {game.marketplaceStatus === 'submitted' ? (
                <span className="px-3 py-1 rounded text-sm bg-yellow-600 text-white">
                  Submitted to Marketplace
                </span>
              ) : (
                <button
                  onClick={() => onSubmitToMarketplace(game)}
                  className="px-3 py-1 rounded text-sm transition-colors bg-green-600 text-white hover:bg-green-700"
                >
                  Submit to Marketplace
                </button>
              )}
            </>
          )}

          <button
            onClick={() => onDelete(game)}
            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>

        {/* Date Info */}
        <div className="mt-4 text-gray-500 text-xs">
          {type === 'purchased' ? (
            `Purchased: ${game.purchasedAt?.toDate?.().toLocaleDateString() || 'Unknown'}`
          ) : (
            `Created: ${game.createdAt?.toDate?.().toLocaleDateString() || 'Unknown'}`
          )}
        </div>
      </div>
    </div>
  );
};

const MyGames = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, ready, claims } = useAuth();
  const [games, setGames] = useState({
    active: [], // Active purchasable games (can be launched)
    created: [],
    pending: [],
    marketplace: [],
    rejected: [],
    previouslyPurchased: [] // Completed games
  });
  const [userMeta, setUserMeta] = useState(null);
  const [activeSessionsByGameId, setActiveSessionsByGameId] = useState({});
  const [launchWindows, setLaunchWindows] = useState({});
  const [launchModalState, setLaunchModalState] = useState({
    isOpen: false,
    action: 'confirm_start'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Removed entitlements state - not needed for simplified MVP
  const [selectedGame, setSelectedGame] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [, forceTick] = useState(0); // 60s ticker to keep countdown fresh

  // Helper functions (no DB reads)
  const msLeft = (expiresAt) => {
    if (!expiresAt) return 0;
    const t = typeof expiresAt.toMillis === 'function' ? expiresAt.toMillis() : +expiresAt;
    return Math.max(0, t - Date.now());
  };
  
  const fmtLeft = (ms) => {
    const m = Math.ceil(ms / 60000);
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h ${mm}m`;
  };

  // Set active tab from URL parameter and check for resume required
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const resumeRequired = urlParams.get('resumeRequired');
    
    if (tabParam && ['active', 'created', 'pending', 'marketplace', 'rejected', 'previouslyPurchased'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    
    // Show helpful message if user was redirected from direct launch attempt
    if (resumeRequired === '1') {
      console.log('🎯 User redirected from direct launch attempt - showing created games tab');
      setActiveTab('created');
    }
  }, [location.search]);

  // Lightweight ticker to refresh "Xh Ym" labels without reads
  useEffect(() => {
    const id = setInterval(() => forceTick(x => x + 1), 60000);
    return () => clearInterval(id);
  }, []);



  useEffect(() => {
    let unsubscribeEntitlements = null;

    const loadGames = async () => {
      console.log('🎮 MY GAMES: useEffect triggered, user:', user?.uid);
      if (!user) {
        console.log('🎮 MY GAMES: No user, returning early');
        return;
      }

      try {
        setLoading(true);
        
        // No entitlements needed for simplified MVP approach
        console.log('🎮 MY GAMES: Starting games loading...');

        // Load user's created games
        console.log('🎮 Loading created games for user:', user.uid);
        let createdGamesData = [];
        try {
          const createdGamesQuery = query(
            collection(db, 'games'),
            where('ownerId', '==', user.uid)
          );
          
          const createdGamesSnapshot = await getDocs(createdGamesQuery);
          createdGamesData = createdGamesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('🎮 Loaded created games:', createdGamesData.length);
        } catch (createdError) {
          console.error('Error loading created games:', createdError);
          // Continue without created games if there's an error
        }

        // Load user's marketplace submissions (for pending approval tab)
        console.log('🎮 Loading marketplace submissions for user:', user.uid);
        let marketplaceSubmissions = [];
        try {
          const marketplaceQuery = query(
            collection(db, 'games', 'content', 'triviaGames'),
            where('ownerId', '==', user.uid),
            where('status', '==', 'pending_approval')
          );
          
          const marketplaceSnapshot = await getDocs(marketplaceQuery);
          marketplaceSubmissions = marketplaceSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('🎮 Loaded marketplace submissions:', marketplaceSubmissions.length);
        } catch (marketplaceError) {
          console.error('Error loading marketplace submissions:', marketplaceError);
          // Continue without marketplace submissions if there's an error
        }

        // Note: Users cannot save drafts - they must complete games

        // Load user's purchased games from new library system
        console.log('🛒 Loading purchased games for user:', user.uid);
        console.info("MY GAMES: querying library for uid=", user.uid);
        console.info("MY GAMES: user object:", user);
        console.info("MY GAMES: auth current user:", auth.currentUser);
        
        // Check if user is properly authenticated
        if (!user || !user.uid) {
          console.error("MY GAMES: No user or user.uid found");
          throw new Error("User not authenticated");
        }
        
        // Check for UID mismatch
        if (auth.currentUser && auth.currentUser.uid !== user.uid) {
          console.error("MY GAMES: UID MISMATCH - auth.currentUser.uid:", auth.currentUser.uid, "vs user.uid:", user.uid);
        } else {
          console.info("MY GAMES: UID match confirmed - both are:", user.uid);
        }
        
        // Use authenticated user's UID directly
        const authenticatedUid = auth.currentUser?.uid || user.uid;
        const libraryPath = `users/${authenticatedUid}/library`;
        console.info("MY GAMES: library path:", libraryPath);
        console.info("MY GAMES: using authenticated UID:", authenticatedUid);
        
        // Test basic read access first
        try {
          console.info("MY GAMES: testing basic user document read...");
          const userDoc = await getDoc(doc(db, `users/${authenticatedUid}`));
          console.info("MY GAMES: user document exists:", userDoc.exists());
          
          // Check if library subcollection exists
          if (userDoc.exists()) {
            const data = userDoc.data() || {};
            setUserMeta(data);
            setActiveSessionsByGameId(data.activeSessions || {});
            console.info("MY GAMES: user document data keys:", Object.keys(data));
            console.log('🧭 ACTIVE SESSIONS SNAPSHOT:', data.activeSessions || {});
            console.log('🧭 ACTIVE SESSIONS TYPE:', typeof data.activeSessions);
            console.log('🧭 ACTIVE SESSIONS KEYS:', data.activeSessions ? Object.keys(data.activeSessions) : 'null');
            
            // Debug subscription and role info
            console.log('🔍 USER ROLE DEBUG:', {
              role: data.role,
              subscription: data.subscription,
              plan: data.plan,
              // entitlements removed for simplified MVP
            });
          }
        } catch (userError) {
          console.error("MY GAMES: Error reading user document:", userError);
        }
        
        // Test if we can read the library collection at all (without ordering)
        try {
          console.info("MY GAMES: testing basic library collection read...");
          const basicLibraryQuery = query(collection(db, libraryPath));
          const basicLibrarySnapshot = await getDocs(basicLibraryQuery);
          console.info("MY GAMES: basic library query successful, count:", basicLibrarySnapshot.docs.length);
        } catch (basicError) {
          console.error("MY GAMES: Basic library query failed:", basicError);
          
          // Try to read a specific document to see if that works
          try {
            console.info("MY GAMES: trying to read a specific library document...");
            // This will fail if no documents exist, but will show if it's a permissions issue
            const testDoc = await getDoc(doc(db, `${libraryPath}/test-doc`));
            console.info("MY GAMES: specific document read result:", testDoc.exists());
          } catch (docError) {
            console.error("MY GAMES: Specific document read failed:", docError);
          }
        }
        
        // Now try with ordering
        const libraryQuery = query(
          collection(db, libraryPath),
          orderBy("addedAt", "desc")
        );
        
        console.info("MY GAMES: about to execute ordered library query...");
        const librarySnapshot = await getDocs(libraryQuery);
        console.info("MY GAMES: ordered library query executed successfully");
        
        const purchasedGamesData = librarySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.info("MY GAMES: library count", purchasedGamesData.length, purchasedGamesData);
        console.log('🛒 MY GAMES: Found', purchasedGamesData.length, 'purchased games for user', user.uid);
        
        // Fetch game details for each purchased game
        const purchasedGamesWithDetails = [];
        for (const game of purchasedGamesData) {
          try {
            // Fetch the actual game data from marketplace
            const gameDoc = await getDoc(doc(db, `games/content/triviaGames/${game.id}`));
            if (gameDoc.exists()) {
              const gameData = gameDoc.data();
              purchasedGamesWithDetails.push({
                ...game,
                title: gameData.title || 'Unknown Game',
                description: gameData.description,
                categories: gameData.categories,
                difficulty: gameData.difficulty,
                imageUrl: gameData.imageUrl
              });
            } else {
              // Fallback if game not found in marketplace
              purchasedGamesWithDetails.push({
                ...game,
                title: 'Unknown Game',
                description: 'Game details not available'
              });
            }
          } catch (error) {
            console.warn('Could not fetch game details for', game.id, error);
            purchasedGamesWithDetails.push({
              ...game,
              title: 'Unknown Game',
              description: 'Game details not available'
            });
          }
        }
        
        purchasedGamesWithDetails.forEach(game => {
          console.log('🛒 MY GAMES: Purchased game:', game.title, 'ID:', game.id, 'Source:', game.source, 'AddedAt:', game.addedAt);
        });

        // Separate active and completed purchased games
        const activeGames = [];
        const completedGames = [];
        
        for (const game of purchasedGamesWithDetails) {
          try {
            // Use the document ID as the gameId (it's the actual game ID)
            const gameId = game.id;
            const launchStatus = await getLaunchStatus(gameId);
            console.log('🎮 Launch status for', game.title || 'Unknown game', ':', launchStatus);
            if (launchStatus.status === 'completed') {
              completedGames.push(game);
            } else {
              activeGames.push(game);
            }
          } catch (error) {
            console.warn('Could not check launch status for game', game.id, error);
            // If we can't check status, assume it's active
            activeGames.push(game);
          }
        }

        console.log('🎮 MY GAMES: Active games:', activeGames.length, 'Completed games:', completedGames.length);
        console.log('🎮 MY GAMES: Total games loaded - Created:', createdGamesData.length, 'Purchased:', purchasedGamesData.length);

        // Categorize created games
        const categorizedGames = {
          active: activeGames,
          created: [],
          pending: [],
          marketplace: [],
          rejected: [],
          previouslyPurchased: completedGames
        };

        createdGamesData.forEach(game => {
          if (game.status === 'pending_approval' || game.approvalStatus === 'pending') {
            categorizedGames.pending.push(game);
          } else if (game.status === 'approved' || game.approvalStatus === 'approved') {
            categorizedGames.marketplace.push(game);
          } else if (game.status === 'rejected' || game.approvalStatus === 'rejected') {
            categorizedGames.rejected.push(game);
          } else {
            categorizedGames.created.push(game);
          }
        });

        // Add marketplace submissions to pending tab
        marketplaceSubmissions.forEach(game => {
          categorizedGames.pending.push(game);
        });

        setGames(categorizedGames);
        console.log('🕐 Games set successfully, active sessions already captured from user doc');
        console.log('🎮 MY GAMES: Games loading completed, setting loading to false');
        
        // Load launch windows for purchased, created, and marketplace submission games
        await loadLaunchWindows([...purchasedGamesData, ...createdGamesData, ...marketplaceSubmissions]);
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading games:', error);
        // Set user-friendly error message
        if (error.code === 'permission-denied') {
          setError('You don\'t have permission to view this page. Please make sure you\'re logged in.');
        } else if (error.code === 'unavailable') {
          setError('Unable to connect to the server. Please check your internet connection and try again.');
        } else if (error.message?.includes('Missing or insufficient permissions')) {
          setError('Unable to load your games. Please refresh the page or contact support if this continues.');
        } else {
          setError('Failed to load your games. Please try refreshing the page.');
        }
        setLoading(false);
      }
    };

    loadGames();

    // Cleanup function for the useEffect
    return () => {
      // No entitlements cleanup needed
    };
  }, [user]);

  const handlePreview = (gameId) => {
    navigate(`/preview/${gameId}`);
  };

  const handleEdit = (gameId, isDraft = false) => {
    if (isDraft) {
      navigate(`/add-game/${gameId}`);
    } else {
      navigate(`/edit-game/${gameId}`);
    }
  };

  // Load launch windows for both purchased and created games
  const loadLaunchWindows = async (games) => {
    console.log('🕐 Loading launch windows for', games.length, 'games');
    const windows = {};
    
    for (const game of games) {
      try {
        // Handle both created games (game.id) and purchased games (game.gameId)
        const gameId = game.gameId || game.id;
        const launchWindow = null; // await launchSessionManager.getLaunchStatus(gameId);
        if (launchWindow) {
          windows[gameId] = launchWindow;
        }
      } catch (error) {
        const gameId = game.gameId || game.id;
        console.error(`Error loading launch window for game ${gameId}:`, error);
      }
    }
    
    setLaunchWindows(windows);
    console.log('🕐 Launch windows loaded:', Object.keys(windows).length);
  };

  // Central handler for launch button clicks
  const handleLaunchClick = async (game) => {
    // Handle both created games (game.id) and purchased games (game.gameId)
    const gameId = game.gameId || game.id;
    const launchWindow = launchWindows[gameId];
    
    if (!launchWindow) {
      console.error('No launch window found for game:', gameId);
      return;
    }

    switch (launchWindow.status) {
      case 'not_started':
        // Show confirmation modal
        setLaunchModalState({
          isOpen: true,
          purchaseId: game.id,
          gameId: game.gameId,
          gameTitle: game.title,
          status: 'not_started',
          action: 'confirm_start'
        });
        break;
        
      case 'active':
        // Show active session modal
        setLaunchModalState({
          isOpen: true,
          purchaseId: game.id,
          gameId: game.gameId,
          gameTitle: game.title,
          status: 'active',
          timeRemaining: "00:00:00", // launchSessionManager.getTimeRemaining(launchWindow.expiresAt),
          action: 'show_active'
        });
        break;
        
      case 'expired':
        // Show buy again modal
        setLaunchModalState({
          isOpen: true,
          purchaseId: game.id,
          gameId: game.gameId,
          gameTitle: game.title,
          status: 'expired',
          action: 'show_buy_again'
        });
        break;
    }
  };

  // Launch modal handlers
  const handleLaunchConfirm = async () => {
    const { purchaseId, gameId, gameTitle } = launchModalState;
    
    try {
      const session = null; // await launchSessionManager.startLaunchSession(purchaseId, gameId, gameTitle);
      
      if (session) {
        // Update launch windows state
        setLaunchWindows(prev => ({
          ...prev,
          [gameId]: {
            purchaseId,
            gameId,
            gameTitle,
            status: 'active',
            startAt: session.startAt,
            expiresAt: session.expiresAt,
            sessionToken: session.sessionToken
          }
        }));
        
        // Store session info for LaunchGame to show 12-hour warning
        const sessionInfo = {
          sessionId: session.sessionId,
          gameId,
          startedAt: session.startAt,
          expiresAt: session.expiresAt,
          isNewSession: true,
          sessionToken: session.sessionToken
        };
        localStorage.setItem(`launchSession_${gameId}`, JSON.stringify(sessionInfo));
        
        // Close modal and navigate
        setLaunchModalState({ isOpen: false, action: 'confirm_start' });
        navigate(`/launch-game/${gameId}`, { state: { session: sessionInfo }});
      }
    } catch (error) {
      console.error('Error starting launch session:', error);
      alert(error.message || 'Failed to start launch session');
    }
  };

  const handleLaunchContinue = () => {
    const { gameId, sessionToken } = launchModalState;
    const launchWindow = launchWindows[gameId];
    
    // Store session info for LaunchGame
    if (launchWindow) {
      const sessionInfo = {
        sessionId: launchWindow.sessionId,
        gameId,
        startedAt: launchWindow.startAt,
        expiresAt: launchWindow.expiresAt,
        isNewSession: false,
        sessionToken
      };
      localStorage.setItem(`launchSession_${gameId}`, JSON.stringify(sessionInfo));
      setLaunchModalState({ isOpen: false, action: 'confirm_start' });
      navigate(`/launch-game/${gameId}`, { state: { session: sessionInfo }});
    } else {
      setLaunchModalState({ isOpen: false, action: 'confirm_start' });
      navigate(`/launch-game/${gameId}`, { state: { sessionToken }});
    }
  };

  const handleBuyAgain = () => {
    // Buy again flow is handled by handleRepurchase in LaunchButton component
    setLaunchModalState({ isOpen: false, action: 'confirm_start' });
  };

  const handleSubmitToMarketplace = async (game) => {
    try {
      // No subscription checks needed - all users can submit to marketplace

      // Create a copy of the game for marketplace submission
      const { id, ...gameWithoutId } = game; // Remove id field
      const marketplaceGameData = {
        ...gameWithoutId,
        // Add marketplace-specific fields
        marketplaceData: {
          price: game.price || 20,
          salesCount: 0,
          totalRevenue: 0,
          creatorRevenue: 0,
          triviaPlugRevenue: 0,
          approvalStatus: 'pending',
          approvedAt: null,
          approvedBy: null,
          isApproved: false,
          submittedAt: new Date(),
          submittedBy: user.uid
        },
        status: 'pending_approval',
        isForMarketplace: true,
        visibility: 'marketplace',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Save to marketplace collection
      const marketplaceRef = await addDoc(collection(db, 'games', 'content', 'triviaGames'), marketplaceGameData);
      console.log('✅ Game submitted to marketplace:', marketplaceRef.id);

      // Update original game to mark it as submitted (but keep it editable)
      await updateDoc(doc(db, 'games', game.id), {
        marketplaceStatus: 'submitted',
        marketplaceGameId: marketplaceRef.id, // Link to marketplace version
        submittedAt: new Date(),
        submittedBy: user.uid
      });

      // Refresh games list - keep the game in created games but mark it as submitted
      const updatedGames = { ...games };
      updatedGames.created = updatedGames.created.map(g => 
        g.id === game.id 
          ? { 
              ...g, 
              marketplaceStatus: 'submitted',
              marketplaceGameId: marketplaceRef.id,
              submittedAt: new Date()
            }
          : g
      );
      setGames(updatedGames);

      alert('Game submitted for marketplace review! You can still edit your personal copy.');
    } catch (error) {
      console.error('Error submitting to marketplace:', error);
      alert('Failed to submit game to marketplace. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!selectedGame) return;

    try {
      await deleteDoc(doc(db, 'games', selectedGame.id));
      
      // Remove from local state
      setGames(games.filter(g => g.id !== selectedGame.id));
      setShowDeleteModal(false);
      setSelectedGame(null);
      
      alert('Game deleted successfully!');
    } catch (error) {
      console.error('Error deleting game:', error);
      alert('Failed to delete game. Please try again.');
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading your games...</div>
      </div>
    );
  }

  return (
    <HostLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">🎮 My Games</h1>
          <p className="text-gray-400">Manage your created and purchased games</p>
        </div>

        {/* Simple Pricing Info */}
        <div className="mb-6 p-4 bg-gray-800 rounded-lg">
          <h2 className="text-lg font-semibold mb-3">💰 Pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p><strong>Created Games:</strong> $15 per launch</p>
              <p><strong>Game Creation:</strong> Free</p>
            </div>
            <div>
              <p><strong>Marketplace Games:</strong> $35 total ($20+ base + $15 launch fee)</p>
              <p><strong>Launch Window:</strong> 12 hours</p>
            </div>
            <div>
              <p><strong>Revenue Share:</strong> 50% of base price to creator</p>
              <p><strong>Launch Fee:</strong> $15 (100% to TriviaPlug)</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 border-b border-gray-700">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'active'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              🎮 Active ({games.active.length})
            </button>
            <button
              onClick={() => setActiveTab('created')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'created'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              📝 Created Games ({games.created.length})
            </button>
            <button
              onClick={() => setActiveTab('previouslyPurchased')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'previouslyPurchased'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              🏆 Previously Purchased ({games.previouslyPurchased.length})
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'pending'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              ⏳ Pending Approval ({games.pending.length})
            </button>
            <button
              onClick={() => setActiveTab('marketplace')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'marketplace'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              ✅ Marketplace Games ({games.marketplace.length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                activeTab === 'rejected'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              ❌ Rejected Games ({games.rejected.length})
            </button>
          </div>
        </div>

        {/* Helpful message for users redirected from direct launch attempt */}
        {new URLSearchParams(location.search).get('resumeRequired') === '1' && (
          <div className="mb-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
            <div className="flex items-start">
              <div className="text-blue-400 text-xl mr-3">ℹ️</div>
              <div>
                <h3 className="text-blue-300 font-semibold mb-1">Ready to Launch Your Game?</h3>
                <p className="text-gray-300 text-sm">
                  To launch your created game, click the "Launch" button below and pay $15 for a 12-hour launch window. 
                  This gives you full access to host your trivia game live.
                </p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading your games...</p>
          </div>
        ) : error ? (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-red-300 font-semibold mb-2">Unable to Load Games</h3>
                <p className="text-red-200 text-sm mb-4">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    window.location.reload();
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {/* Active Games Tab */}
            {activeTab === 'active' && (
              <div>
                {games.active.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🎮</div>
                    <h2 className="text-2xl font-semibold mb-2">No Active Games</h2>
                    <p className="text-gray-400 mb-6">You don't have any games ready to launch right now.</p>
                    <button
                      onClick={() => navigate('/marketplace')}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Browse Marketplace
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {games.active.map((game) => (
                      <GameCard
                        key={game.id}
                        game={game}
                        type="purchased"
                        launchWindow={launchWindows[game.gameId]}
                        onPreview={() => handlePreview(game.gameId)}
                        onEdit={() => handleEdit(game.gameId)}
                        onLaunch={handleLaunchClick}
                        onDelete={(game) => {
                          setSelectedGame(game);
                          setShowDeleteModal(true);
                        }}
                        navigate={navigate}
                        user={user}
                        claims={claims}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Created Games Tab */}
            {activeTab === 'created' && (
              <div>
                {games.created.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📝</div>
                    <h2 className="text-2xl font-semibold mb-2">No Created Games</h2>
                    <p className="text-gray-400 mb-6">Start creating amazing trivia games!</p>
                    <button
                      onClick={() => navigate('/add-game')}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Create Your First Game
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {games.created.map((game) => {
                      console.log('🎮 Rendering created game:', { id: game.id, title: game.title });
                      return (
                        <GameCard 
                          key={game.id} 
                          game={game} 
                          type="created"
                          launchWindow={launchWindows[game.id]}
                          onPreview={handlePreview}
                          onEdit={handleEdit}
                          onLaunch={handleLaunchClick}
                          onSubmitToMarketplace={handleSubmitToMarketplace}
                          onDelete={(game) => {
                          setSelectedGame(game);
                          setShowDeleteModal(true);
                        }}
                        navigate={navigate}
                        user={user}
                        claims={claims}
                      />
                      );
                    })}
                  </div>
                )}
              </div>
            )}


            {/* Pending Approval Tab */}
            {activeTab === 'pending' && (
              <div>
                {games.pending.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">⏳</div>
                    <h2 className="text-2xl font-semibold mb-2">No Pending Games</h2>
                    <p className="text-gray-400">Games submitted for marketplace approval will appear here.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {games.pending.map((game) => (
                      <GameCard 
                        key={game.id} 
                        game={game} 
                        type="pending"
                        onPreview={handlePreview}
                        onEdit={handleEdit}
                        onDelete={(game) => {
                          setSelectedGame(game);
                          setShowDeleteModal(true);
                        }}
                        navigate={navigate}
                        user={user}
                        claims={claims}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Marketplace Games Tab */}
            {activeTab === 'marketplace' && (
              <div>
                {games.marketplace.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">✅</div>
                    <h2 className="text-2xl font-semibold mb-2">No Marketplace Games</h2>
                    <p className="text-gray-400">Your approved marketplace games will appear here.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {games.marketplace.map((game) => (
                      <GameCard 
                        key={game.id} 
                        game={game} 
                        type="marketplace"
                        onPreview={handlePreview}
                        onEdit={handleEdit}
                        onDelete={(game) => {
                          setSelectedGame(game);
                          setShowDeleteModal(true);
                        }}
                        navigate={navigate}
                        user={user}
                        claims={claims}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Rejected Games Tab */}
            {activeTab === 'rejected' && (
              <div>
                {games.rejected.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">❌</div>
                    <h2 className="text-2xl font-semibold mb-2">No Rejected Games</h2>
                    <p className="text-gray-400">Games that failed marketplace review will appear here.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {games.rejected.map((game) => (
                      <GameCard 
                        key={game.id} 
                        game={game} 
                        type="rejected"
                        onPreview={handlePreview}
                        onEdit={handleEdit}
                        onDelete={(game) => {
                          setSelectedGame(game);
                          setShowDeleteModal(true);
                        }}
                        navigate={navigate}
                        user={user}
                        claims={claims}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Previously Purchased Tab */}
            {activeTab === 'previouslyPurchased' && (
              <div>
                {games.previouslyPurchased.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🏆</div>
                    <h2 className="text-2xl font-semibold mb-2">No Completed Games</h2>
                    <p className="text-gray-400 mb-6">Games you've completed and submitted will appear here.</p>
                    <button
                      onClick={() => setActiveTab('active')}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View Active Games
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {games.previouslyPurchased.map((purchase) => (
                      <GameCard 
                        key={purchase.id} 
                        game={purchase} 
                        type="purchased"
                        launchWindow={launchWindows[purchase.gameId]}
                        onPreview={() => handlePreview(purchase.gameId)}
                        onEdit={() => handleEdit(purchase.gameId)}
                        onLaunch={handleLaunchClick}
                        onDelete={(game) => {
                          setSelectedGame(game);
                          setShowDeleteModal(true);
                        }}
                        navigate={navigate}
                        user={user}
                        claims={claims}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Delete Game</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to delete "{selectedGame.title}"? This action cannot be undone.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedGame(null);
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Launch Modal - temporarily disabled */}
      {/* <LaunchModal
        isOpen={launchModalState.isOpen}
        onClose={() => setLaunchModalState({ isOpen: false, action: 'confirm_start' })}
        onConfirm={handleLaunchConfirm}
        onBuyAgain={handleBuyAgain}
        state={launchModalState}
      /> */}
    </HostLayout>
  );
};

export default MyGames; 